// Form field primitives — the design's labeled inputs/selects: 10px radius,
// hairline border, focus ring in accent. `deep` uses the darker in-card well.

import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { useId } from 'react';

interface FieldProps {
  label: string;
  children: ReactNode;
  /** Slot on the label row's right (e.g. "Forgot password?"). */
  labelEnd?: ReactNode;
}

export function Field({ label, labelEnd, children }: FieldProps) {
  return (
    <div className="bz-field">
      <div className="bz-field-labelrow">
        <span className="bz-field-label">{label}</span>
        {labelEnd}
      </div>
      {children}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  deep?: boolean;
}

export function TextInput({ deep = false, className, ...rest }: InputProps) {
  const cls = ['bz-input', deep ? 'bz-input-deep' : '', className ?? ''].filter(Boolean).join(' ');
  return <input className={cls} {...rest} />;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  deep?: boolean;
}

export function Select({ deep = false, className, ...rest }: SelectProps) {
  const cls = ['bz-input', 'bz-select', deep ? 'bz-input-deep' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return <select className={cls} {...rest} />;
}

/** Labeled input with an auto-wired id/htmlFor pair (a11y + e2e getByLabel).
 *  `end` renders a right-side adornment inside the input well (e.g. an action). */
export function LabeledInput({
  label,
  deep,
  end,
  ...rest
}: InputProps & { label: string; end?: ReactNode }) {
  const id = useId();
  const input = <TextInput id={id} deep={deep} {...rest} />;
  return (
    <div className="bz-field">
      <div className="bz-field-labelrow">
        <label className="bz-field-label" htmlFor={id}>
          {label}
        </label>
      </div>
      {end ? (
        <div className="bz-input-adorned">
          {input}
          {end}
        </div>
      ) : (
        input
      )}
    </div>
  );
}
